import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '@/api/client';
import type { Page } from '@/api/types';
import { useFormFactor } from '@/lib/formFactor';
import { useSession } from '@/stores/session';
import { Icon } from '@/components/Icon';
import { Button, IconButton } from '@/components/Button';
import { Tag } from '@/components/Tag';
import { Empty, ErrorState, Skeleton } from '@/components/States';
import { ViewSidebar, type SidebarView } from '@/components/ViewSidebar';
import { dateTime, qty } from '@/lib/format';
import { Applied, Chips, DataTable, Denied, FilterButton, k, Pager, PhoneBar, SearchBox, Sentinel, ViewToggle, type ChipItem, type Col, type ViewMode } from './ListKit';
import { FilterSheet } from './FilterSheet';
import { TxAvailable, TxList, TxQty, TxSource, hasSource } from './TxCards';
import { daysAgo, usePagedList, useUrlState, useWarehouses } from './api';
import { InventoryActionsSheet } from './InventoryActionsSheet';
import { signedQuantity, transactionKind, txRanges, txSources } from './status';
import type { InventoryTransaction, TxSource as TxSourceKey } from './types';

const DEFAULT_RANGE = '30';

function csvCell(value: string | number) {
  return `"${String(value).replaceAll('"', '""')}"`;
}

/**
 * 出入库流水（老系统 /inventory/transactions）。
 * - 手机：二级页（返回库存），按天分组的卡片，可切表格；无限滚动。
 * - iPad：表格（默认），可切卡片；分页。桌面：表格 + 分页。
 * 类型按老系统 getTransactionTypeTag 判定；来源 chips = 接口 source 参数；时间范围默认近 30 天（同老版）。
 */
export function TransactionsPage() {
  const navigate = useNavigate();
  const ff = useFormFactor();
  const can = useSession((st) => st.can);
  const allowed = can('inventory.transactions.read');
  const [sp, set] = useUrlState();
  const source = (sp.get('source') as TxSourceKey) || 'all';
  const range = sp.get('range') || DEFAULT_RANGE;
  const q = sp.get('q') || '';
  const warehouseId = sp.get('warehouseId') || undefined;
  const productId = sp.get('productId') || undefined;
  const pname = sp.get('pname') || '指定产品';
  const page = Number(sp.get('page')) || 1;
  const phone = ff === 'phone';
  const [preferredPhoneMode, setPreferredPhoneMode] = useState<ViewMode>(() => {
    try { return localStorage.getItem('v2:transactions:phone-mode') === 'table' ? 'table' : 'cards'; }
    catch { return 'cards'; }
  });
  const mode: ViewMode = ff === 'desktop' ? 'table' : ((sp.get('mode') as ViewMode) || (phone ? preferredPhoneMode : 'table'));
  const pageSize = phone ? 30 : ff === 'tablet' || ff === 'desktop' ? 8 : 50;
  const [panel, setPanel] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [searching, setSearching] = useState(!!q);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportError, setExportError] = useState('');

  const rangeDef = txRanges.find((r) => r.key === range) || txRanges[1];
  const query = useMemo(
    () => ({
      source: source === 'all' ? undefined : source,
      keyword: q || undefined,
      warehouseId,
      productId,
      ...(rangeDef.days ? { from: daysAgo(rangeDef.days), to: daysAgo(0) } : { all: true }),
    }),
    [source, q, warehouseId, productId, rangeDef],
  );
  const list = usePagedList<InventoryTransaction>(allowed ? '/api/v2/inventory/transactions' : null, query, { pageSize, append: phone, page });
  const warehouses = useWarehouses(allowed);
  const canAdjust = import.meta.env.VITE_READONLY === '0' && can('inventory.adjust.write');
  const canInbound = import.meta.env.VITE_READONLY === '0' && can('inventory.in.write');
  const canImport = import.meta.env.VITE_READONLY === '0' && can('inventory.import.initial_stock');
  const canOutbound = import.meta.env.VITE_READONLY === '0' && can('inventory.out.write');

  if (!allowed) return <Denied what="查看出入库流水" perm="查看库存流水" />;

  const chips: ChipItem[] = txSources.map((x) => ({
    key: x.key,
    label: x.label,
    count: x.key === source ? list.total : undefined,
    active: x.key === source,
    onClick: () => set({ source: x.key === 'all' ? undefined : x.key, page: undefined }),
  }));
  const desktopInventoryNav: SidebarView[] = [
    { key: 'all', label: '库存总览', icon: 'box', to: '/inventory' },
    { key: 'warehouse', label: '按仓库明细', icon: 'bank', to: '/inventory/warehouses' },
    { key: 'change', label: '变动汇总', icon: 'chart', to: '/inventory/changes' },
    { key: 'shortage', label: '负可用', icon: 'alert', separator: true, hot: true, to: '/inventory/shortage' },
    { key: 'transit', label: '在途待入库', icon: 'inbox', ...(can('purchase.read') ? { to: '/inventory/transit' } : { disabledReason: '当前账号没有采购单查看权限' }) },
    { key: 'noStock', label: '零库存', icon: 'flag', to: '/inventory?view=noStock' },
    { key: 'tx', label: '出入库流水', icon: 'book', count: list.total, separator: true, to: '/inventory/transactions' },
    ...(can('imports.read') ? [{ key: 'imports', label: '导入历史', icon: 'down', to: '/imports' }] : []),
  ];
  const actionsSheet = actionsOpen && <InventoryActionsSheet
    warehouseName={warehouses.find((warehouse) => warehouse.id === warehouseId)?.name || warehouses.find((warehouse) => warehouse.isDefaultInbound)?.name || warehouses[0]?.name}
    allowed={{ inbound: canInbound, outbound: canOutbound, adjust: canAdjust, import: canImport }}
    onClose={() => setActionsOpen(false)}
    onSelect={(action) => { setActionsOpen(false); navigate(`/inventory?action=${action}`); }}
  />;
  const showInventoryActions = canAdjust || canInbound || canImport || canOutbound;
  const filterCount = Number(!!warehouseId) + Number(range !== DEFAULT_RANGE);
  const exportAll = async () => {
    if (exportBusy || !list.total) return;
    setExportBusy(true); setExportError('');
    try {
      const rows: InventoryTransaction[] = [];
      let currentPage = 1;
      for (;;) {
        const result = await api.get<Page<InventoryTransaction>>('/api/v2/inventory/transactions', { ...query, page: currentPage, pageSize: 200 });
        rows.push(...result.items);
        if (!result.pagination.hasNext) break;
        currentPage += 1;
      }
      const lines = [
        ['时间', '类型', '产品编号', '产品', '仓库', '数量', '变动后可用', '来源单据', '操作人'],
        ...rows.map((transaction) => {
          const quantity = signedQuantity(transaction);
          const source = transaction.sourceShipment?.shipmentNo || transaction.sourceOrder?.orderNo || transaction.sourcePurchaseOrder?.purchaseNo || transaction.sourceTaobaoReturn?.returnNo || transaction.sourceTaobaoOrder?.orderNo || transaction.note || '';
          return [dateTime(transaction.createdAt), transactionKind(transaction).label, transaction.product.code, transaction.product.name, transaction.warehouse.name, `${quantity.sign}${qty(quantity.value)}`, transaction.availableAfter == null ? '—' : qty(transaction.availableAfter), source, transaction.createdBy?.name || ''];
        }),
      ];
      const csv = `\uFEFF${lines.map((line) => line.map(csvCell).join(',')).join('\r\n')}`;
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const anchor = document.createElement('a');anchor.href = url;anchor.download = `出入库流水-${new Date().toISOString().slice(0, 10)}.csv`;document.body.append(anchor);anchor.click();anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      setExportError(error instanceof Error ? error.message : '导出流水失败');
    } finally { setExportBusy(false); }
  };

  const applied = (
    <Applied
      items={[
        ...(productId ? [{ key: 'product', label: pname, onRemove: () => set({ productId: undefined, pname: undefined, page: undefined }) }] : []),
        ...(range !== DEFAULT_RANGE ? [{ key: 'range', label: rangeDef.label, onRemove: () => set({ range: undefined, page: undefined }) }] : []),
        ...(warehouseId ? [{ key: 'wh', label: warehouses.find((w) => w.id === warehouseId)?.name || '指定仓库', onRemove: () => set({ warehouseId: undefined, page: undefined }) }] : []),
      ]}
      onClear={() => set({ productId: undefined, pname: undefined, range: undefined, warehouseId: undefined, page: undefined })}
    />
  );

  const search = (cls?: string) => (
    <SearchBox className={cls} value={q} autoFocus={phone && searching && !q} placeholder="产品 / 订单号 / 发货单 / 采购单 / 备注" onSearch={(v) => set({ q: v || undefined, page: undefined })} />
  );

  const panelEl = panel && (
    <FilterSheet
      sheet={phone}
      value={{ range, warehouseId }}
      onClose={() => setPanel(false)}
      onApply={(f) => {
        setPanel(false);
        set({ range: f.range === DEFAULT_RANGE ? undefined : f.range, warehouseId: f.warehouseId, page: undefined });
      }}
      groups={[
        { key: 'range', title: '时间范围', required: true, opts: txRanges.map((r) => ({ value: r.key, label: r.label })) },
        { key: 'warehouseId', title: '仓库', opts: warehouses.map((w) => ({ value: w.id, label: w.name })) },
      ]}
      note="类型在上方切换。默认看近 30 天，与老版一致。"
    />
  );

  const body = (() => {
    if (list.error && !list.items.length) {
      return <ErrorState title="流水加载失败" message={list.error.message} detail="GET /api/v2/inventory/transactions" onRetry={list.reload} />;
    }
    if (list.loading && !list.items.length) return <div className={k.pad}><Skeleton lines={3} /><Skeleton lines={3} /><Skeleton lines={3} /></div>;
    if (!list.items.length) {
      return (
        <Empty icon="list" title={`${rangeDef.label}没有符合条件的流水`}>
          {rangeDef.days ? '可以在「筛选」里把时间范围放宽，或者换个关键词。' : '换个关键词，或者清空筛选再看看。'}
        </Empty>
      );
    }
    return null;
  })();

  const wide: Col<InventoryTransaction>[] = [
    { key: 'time', title: '时间', w: 116, fix: true, render: (t) => <span className="tnum">{dateTime(t.createdAt)}</span> },
    { key: 'type', title: '类型', w: 132, render: (t) => { const x = transactionKind(t); return <Tag tone={x.tone} sm>{x.label}</Tag>; } },
    { key: 'product', title: '产品', w: 230, render: (t) => <>{t.product.name} <span className="em tnum">{t.product.code}</span></> },
    { key: 'wh', title: '仓库', w: 100, render: (t) => t.warehouse.name },
    { key: 'qty', title: '数量', w: 90, align: 'r', render: (t) => <TxQty t={t} /> },
    { key: 'after', title: '变动后可用', w: 112, align: 'r', render: (t) => <TxAvailable t={t} /> },
    { key: 'src', title: '来源单据', w: 210, render: (t) => (hasSource(t) ? <TxSource t={t} /> : <span className="em">—</span>) },
    { key: 'by', title: '操作人', w: 90, render: (t) => t.createdBy?.name || <span className="em">—</span> },
  ];
  const narrow: Col<InventoryTransaction>[] = [
    { key: 'p', title: '产品 · 时间', w: 150, fix: true, render: (t) => <div className={k.cell2}><span>{t.product.name}</span><small className="tnum">{dateTime(t.createdAt)}</small></div> },
    { key: 'qty', title: '数量', w: 72, align: 'r', render: (t) => <TxQty t={t} /> },
    { key: 'after', title: '变动后可用', w: 104, align: 'r', render: (t) => <TxAvailable t={t} /> },
    { key: 'type', title: '类型', w: 124, render: (t) => { const x = transactionKind(t); return <Tag tone={x.tone} sm>{x.label}</Tag>; } },
    { key: 'src', title: '来源单据', w: 180, render: (t) => (hasSource(t) ? <TxSource t={t} /> : <span className="em">—</span>) },
    { key: 'wh', title: '仓库', w: 90, render: (t) => t.warehouse.name },
    { key: 'by', title: '操作人', w: 80, render: (t) => t.createdBy?.name || '—' },
    { key: 'code', title: '产品编号', w: 116, render: (t) => <span className="tnum">{t.product.code}</span> },
  ];

  const summary = (
    <>
      {rangeDef.label} · <span className="tnum">{list.total}</span> 条{q && ` · 搜索「${q}」`}
    </>
  );

  /* ------------------------------ 手机 ------------------------------ */
  if (phone) {
    return (
      <div className={k.phone}>
        <PhoneBar title="库存" actions={
          <>
            <IconButton name="srch" label="搜索" onClick={() => setSearching((x) => !x)} />
            {showInventoryActions && <IconButton name="plus" label="库存操作" onClick={() => setActionsOpen(true)} />}
          </>
        } />
        {(searching || q) && <div className={k.psearch}>{search()}</div>}
        <div className={k.chipbar}>
          <button type="button" className={`${k.chip} ${k.navChip}`} onClick={() => setPanel(true)}>{rangeDef.label}<Icon name="chevd" size={14} /></button>
          <Chips items={chips} inset="phone" />
          <FilterButton count={filterCount} onClick={() => setPanel(true)} />
        </div>
        {applied}
        <div className={k.pbody}>
          <div className={k.listbar}>
            <span>{summary}</span>
            <ViewToggle value={mode} onChange={(m) => {
              setPreferredPhoneMode(m);
              try { localStorage.setItem('v2:transactions:phone-mode', m); } catch { /* 本地存储不可用时仍保留当前页选择 */ }
              set({ mode: m === 'cards' ? undefined : m });
            }} />
          </div>
          {body ||
            (mode === 'cards' ? (
              <TxList items={list.items} />
            ) : (
              <>
                <div className={k.pcardTable}>
                  <DataTable dense cols={narrow} rows={list.items} rowKey={(t) => t.id} />
                </div>
                <p className={k.foot}>左右滑动看 8 列。历史可用数无法可靠还原时显示 —。</p>
              </>
            ))}
          <Sentinel hasNext={list.hasNext} loading={list.loading} count={list.items.length} onMore={list.loadMore} />
        </div>
        {panelEl}
        {actionsSheet}
      </div>
    );
  }

  /* --------------------------- iPad / 桌面 --------------------------- */
  if (ff === 'desktop') {
    return (
      <div className={`${k.desk} ${k.desktopPage}`}>
        <header className={k.desktopHead}>
          <div>
            <h1>出入库流水</h1>
            <p>所有库存变动的完整记录，按来源可筛<span>{rangeDef.label}共 {list.total} 条</span></p>
          </div>
          <div className={k.desktopActions}>
            <button type="button" className={k.linkBtn} disabled={exportBusy || !list.total} onClick={() => void exportAll()}>
              <Icon name="down" />{exportBusy ? '导出中…' : '导出'}
            </button>
          </div>
        </header>
        <div className={k.desktopWork}>
          <ViewSidebar title="视图" active="tx" onPick={() => {}} views={desktopInventoryNav} />
          <section className={k.desktopArea}>
            <div className={k.desktopToolbar}>
              {search()}
              <button type="button" className={k.linkBtn} onClick={() => setPanel(true)}><Icon name="cal" />{rangeDef.label}</button>
            </div>
            <div className={`${k.dchips} ${k.txChips}`}><Chips items={chips} /></div>
            {exportError && <p className={k.txError} role="alert">{exportError}</p>}
            {applied}
            {body ? <div className={k.dbody}>{body}</div> : mode === 'cards' ? <div className={k.dlist}><TxList items={list.items} /></div> : <DataTable cols={wide} rows={list.items} rowKey={(t) => t.id} />}
            {!body && <Pager page={list.page} pageSize={pageSize} total={list.total} shown={list.items.length} totalPages={list.totalPages} onGo={(p) => set({ page: p > 1 ? String(p) : undefined })} />}
          </section>
        </div>
        {panelEl}
      </div>
    );
  }

  return (
    <div className={k.desk}>
      <header className={k.dh}>
        <h1>出入库流水<small>{summary}</small></h1>
        <span className={k.sp} />
        {search()}
        {ff === 'tablet'
          ? <button type="button" className={k.linkBtn} onClick={() => setPanel(true)}><Icon name="cal" />{rangeDef.label}</button>
          : <FilterButton boxed count={filterCount} onClick={() => setPanel(true)} />}
        {ff === 'tablet' && <button type="button" className={k.linkBtn} disabled={exportBusy || !list.total} onClick={() => void exportAll()}><Icon name="down" />{exportBusy ? '导出中…' : '导出'}</button>}
        {ff !== 'tablet' && <Button icon="ref" onClick={list.reload}>刷新</Button>}
      </header>
      <div className={k.dchips}><Chips items={chips} inset="desk" /></div>
      {exportError && <p role="alert">{exportError}</p>}
      {applied}
      {body ? (
        <div className={k.dbody}>{body}</div>
      ) : mode === 'cards' ? (
        <div className={k.dlist}><TxList items={list.items} /></div>
      ) : (
        <DataTable cols={wide} rows={list.items} rowKey={(t) => t.id} />
      )}
      {!body && (
        <Pager page={list.page} pageSize={pageSize} total={list.total} shown={list.items.length} totalPages={list.totalPages} onGo={(p) => set({ page: p > 1 ? String(p) : undefined })} />
      )}
      {panelEl}
    </div>
  );
}
